import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from './CurrentUserContext';

const ExpensesContext = createContext(null);

export const useExpenses = () => {
  const context = useContext(ExpensesContext);
  if (!context) throw new Error('useExpenses must be used within an ExpensesProvider');
  return context;
};

// Normalize API response (camelCase) to frontend shape (snake_case for legacy compat)
function normalizeExpense(exp) {
  return {
    id: Number(exp.id),
    title: exp.title,
    amount: Number(exp.amount),
    date: exp.date,
    category: exp.category || 'Other',
    project_id: exp.projectId ?? null,
    contact_id: exp.contactId ?? null,
    receipt_url: exp.receiptUrl ?? null,
    notes: exp.notes ?? '',
    is_billable: exp.isBillable ?? false,
    is_billed: exp.isBilled ?? false,
    tax_rate: exp.taxRate ?? 0,
    created_at: exp.createdAt,
    // TeamBooks P2-3: approval workflow
    user_id: exp.userId ?? null,
    approval_status: exp.approvalStatus ?? 'approved',
    rejection_reason: exp.rejectionReason ?? null,
  };
}

export const ExpensesProvider = ({ children }) => {
  const { currentUser, activeWorkspaceId } = useCurrentUser();
  const [expenses, setExpenses] = useState([]);
  // Block K: a failed load is not an empty list (as InvoicesContext). Expenses reads these.
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const refresh = useCallback(() => {
    api('/api/expenses')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.expenses)) { setExpenses(data.expenses.map(normalizeExpense)); setLoaded(true); setLoadError(null); }
        else setLoadError(new Error('Your expenses did not load.'));   // a 200 with no array is a failure
      })
      .catch(err => { console.error('Failed to load expenses:', err); setLoadError(err); });
  }, []);

  // Refetch on workspace switch too — this provider wraps the AppContent remount
  // boundary, so it must key off activeWorkspaceId (mirrors ContactsContext).
  useEffect(() => {
    // Not before the workspace is known (Carla, 2026-10-11): fetched without one, this ran twice on
    // every load, once for no workspace and again when it arrived.
    if (!currentUser?.id || !activeWorkspaceId) return;
    refresh();
  }, [currentUser?.id, activeWorkspaceId, refresh]);

  useEffect(() => {
    window.addEventListener('aidey:expensesUpdated', refresh);
    return () => window.removeEventListener('aidey:expensesUpdated', refresh);
  }, [refresh]);

  return (
    <ExpensesContext.Provider value={{ expenses, setExpenses, refresh, loaded, loadError }}>
      {children}
    </ExpensesContext.Provider>
  );
};
